

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);

function normalizeText(value) {
  return String(value || '').trim();
}

function normalizeStatus(value) {
  return normalizeText(value).toLowerCase();
}

function clampPercent(value, fallback = 0) {
  const percent = Number(value);
  if (!Number.isFinite(percent)) {
    return fallback;
  }
  return Math.max(0, Math.min(100, Math.round(percent)));
}

function resolveSourceName(source, index, formatters) {
  const profile = Object(source).document_profile && typeof source.document_profile === 'object'
    ? source.document_profile
    : {};
  return normalizeText(
    profile.display_name
    || Object(source).filename
    || Object(source).name
    || Object(source).source_id
    || Object(source).id
    || formatters.formatSourceFallbackName(index),
  );
}

function sourceQueueItem(source, index, formatters) {
  const status = normalizeStatus(source?.status);
  const meta = formatters.resolveSourceStatusMeta(status);
  return {
    id: normalizeText(source?.source_id || source?.id || `source-${index}`),
    name: resolveSourceName(source, index, formatters),
    bucket: meta.bucket,
    label: meta.label,
    progress: meta.progress,
    error: normalizeText(source?.error_message),
  };
}

function uploadQueueItem(item, index, formatters) {
  const stage = normalizeStatus(Object(item).stage);
  const meta = formatters.resolveUploadStageMeta(stage);
  if (!meta) {
    return null;
  }
  return {
    id: normalizeText(Object(item).id || `upload-${index}`),
    name: normalizeText(Object(item).filename || formatters.formatSourceFallbackName(index)),
    bucket: meta.bucket,
    label: meta.label,
    progress: clampPercent(Object(item).progress, meta.bucket === 'failed' ? 100 : 20),
    error: normalizeText(Object(item).error_message),
  };
}

function buildUploadQueueItems(items, formatters) {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map((item, index) => uploadQueueItem(item, index, formatters)).filter(Boolean);
}

function buildSourceQueueItems(items, formatters) {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map((item, index) => sourceQueueItem(item, index, formatters));
}

function countByBucket(items, bucket) {
  return items.filter((item) => item.bucket === bucket).length;
}

function averageProgress(items) {
  if (!items.length) {
    return null;
  }
  const total = items.reduce((sum, item) => sum + clampPercent(item.progress), 0);
  return clampPercent(total / items.length);
}

function buildActivity(counters, progress, formatters) {
  if (counters.running || counters.queued) {
    const state = 'ingesting_sources';
    return {
      state,
      status: 'running',
      headline: formatters.formatSourceActivityHeadline(state, counters),
      progress,
      counters,
    };
  }
  if (counters.failed) {
    const state = 'source_warning';
    return {
      state,
      status: 'warning',
      headline: formatters.formatSourceActivityHeadline(state, counters),
      progress,
      counters,
    };
  }
  if (counters.duplicate) {
    const state = 'source_duplicate';
    return {
      state,
      status: 'warning',
      headline: formatters.formatSourceActivityHeadline(state, counters),
      progress,
      counters,
    };
  }
  if (counters.completed) {
    const state = 'source_ready';
    return {
      state,
      status: 'completed',
      headline: formatters.formatSourceActivityHeadline(state, counters),
      progress,
      counters,
    };
  }
  return null;
}

function resolveCanvasTabLabel(tabKey, canvasTabs, canvasTabLabelsByKey) {
  const normalized = normalizeText(tabKey);
  const tabs = Array.isArray(canvasTabs) ? canvasTabs : [];
  const matchedTab = tabs.find((tab) => normalizeText(Object(tab).key) === normalized);
  const tabLabel = normalizeText(Object(matchedTab).label);
  if (tabLabel) {
    return tabLabel;
  }
  const labelsByKey = canvasTabLabelsByKey && typeof canvasTabLabelsByKey === 'object' && !Array.isArray(canvasTabLabelsByKey)
    ? canvasTabLabelsByKey
    : {};
  return normalizeText(labelsByKey[normalized]) || normalized || 'canvas';
}

function resolveCanvasSyncLabel(backendActivity, formatters) {
  const state = normalizeStatus(backendActivity?.state);
  return formatters.formatCanvasSyncLine(state === 'syncing_canvas');
}

export function buildContextSurfaceLines(options, formatters) {
  const { activeWorkspaceTab, showCanvasPanel, canvasActiveTabKey, canvasTabs, canvasTabLabelsByKey, sourceContextQueue, backendActivity } = { activeWorkspaceTab: 'chats', showCanvasPanel: false, canvasActiveTabKey: '', canvasTabs: [], canvasTabLabelsByKey: {}, sourceContextQueue: null, backendActivity: null, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  if (normalizeStatus(activeWorkspaceTab) === 'sources') {
    return [
      formatters.formatContextViewLine(formatters.resolveWorkspaceViewLabel('sources')),
      formatters.formatSourceStateLine(sourceContextQueue?.activity?.headline),
    ];
  }
  if (showCanvasPanel && normalizeText(canvasActiveTabKey)) {
    return [
      formatters.formatCanvasLine(resolveCanvasTabLabel(canvasActiveTabKey, canvasTabs, canvasTabLabelsByKey)),
      resolveCanvasSyncLabel(backendActivity, formatters),
    ];
  }
  return [formatters.formatContextViewLine(formatters.resolveWorkspaceViewLabel('chats'))];
}

function buildQueueLines(items, counters, formatters) {
  const visibleItems = items.slice(0, 4);
  const lines = [formatters.formatSourceQueueHeader(counters)];
  visibleItems.forEach((item, index) => {
    lines.push(formatters.formatSourceQueueItemLine(index, item));
  });
  if (items.length > visibleItems.length) {
    lines.push(formatters.formatSourceQueueOverflowLine(items.length - visibleItems.length));
  }
  return lines;
}

export function buildSourceContextQueue(options, formatters) {
  const { sourceItems, sourceUploadLoading, sourceUploadProgressItems } = { sourceItems: [], sourceUploadLoading: false, sourceUploadProgressItems: [], ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const uploadItems = buildUploadQueueItems(sourceUploadProgressItems, formatters);
  const sourceQueueItems = buildSourceQueueItems(sourceItems, formatters);
  const fallbackUploadItem = sourceUploadLoading && uploadItems.length === 0
    ? [{ id: 'upload-active', name: formatters.formatFallbackUploadName(), ...formatters.resolveUploadStageMeta('uploading'), progress: 30, error: '' }]
    : [];
  const items = [...fallbackUploadItem, ...uploadItems, ...sourceQueueItems];
  const counters = {
    total: items.length,
    queued: countByBucket(items, 'queued'),
    running: countByBucket(items, 'running'),
    completed: countByBucket(items, 'completed'),
    failed: countByBucket(items, 'failed'),
    duplicate: countByBucket(items, 'duplicate'),
  };
  const activeItems = items.filter((item) => item.bucket === 'running' || item.bucket === 'queued');
  const progress = averageProgress(activeItems.length ? activeItems : items);
  return {
    activity: buildActivity(counters, progress, formatters),
    counters,
    hasQueue: items.length > 0,
    lines: items.length ? buildQueueLines(items, counters, formatters) : [],
    percent: progress,
  };
}

export function resolveSourceContextGaugeProjection(options) {
  const { backendActivity, backendPercent, sourceContextQueue } = { backendActivity: null, backendPercent: null, sourceContextQueue: null, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const sourceActivity = firstTruthy(() => (Object(sourceContextQueue).activity), () => (null));
  const sourceStatus = normalizeStatus(Object(sourceActivity).status);
  const backendStatus = normalizeStatus(Object(backendActivity).status);
  const backendState = normalizeStatus(Object(backendActivity).state);
  const sourceNeedsAttention = everyTruthy(() => (sourceActivity), () => (sourceStatus !== 'completed'));
  const backendNeedsAttention = everyTruthy(() => (backendActivity), () => (backendState !== 'idle'), () => (backendStatus));
  const activity = sourceNeedsAttention
    ? sourceActivity
    : backendNeedsAttention ? backendActivity : (firstTruthy(() => (sourceActivity), () => (backendActivity)));
  // Activity and queue progress remain visible, but neither is a capacity statistic.
  const percent = typeof backendPercent === 'number' && Number.isFinite(backendPercent)
    ? clampPercent(backendPercent) : null;
  return {
    activity,
    headline: normalizeText(Object(activity).headline),
    percent,
  };
}
